import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Copy, CheckCircle, Warning, Download, ArrowSquareOut,
  Lightning, FileText, ArrowRight, ArrowUpRight,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

/** Single field row with a clipboard copy-button. */
const CopyField = ({ label, value, multiline = false, testid }) => {
  const [copied, setCopied] = useState(false);
  const isEmpty = !value || (typeof value === "string" && !value.trim());

  const copy = async () => {
    if (isEmpty) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="border border-zinc-800 rounded-sm overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 bg-zinc-900 px-3 py-2 border-b border-zinc-800">
        <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">{label}</p>
        <button
          onClick={copy}
          disabled={isEmpty}
          className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm transition-colors ${
            isEmpty
              ? "text-zinc-700 cursor-not-allowed"
              : copied
              ? "bg-emerald-500/20 text-emerald-300"
              : "bg-zinc-800 hover:bg-emerald-500 hover:text-black text-zinc-300"
          }`}
          data-testid={`${testid}-copy`}
        >
          {copied ? <><CheckCircle weight="fill" className="w-3 h-3 inline mr-1" />Copied</> : <><Copy className="w-3 h-3 inline mr-1" />Copy</>}
        </button>
      </div>
      <div className="px-3 py-2">
        {isEmpty ? (
          <p className="text-xs text-zinc-600 italic">Not set — <Link to="/profile" className="text-emerald-400 underline">add it to your profile</Link></p>
        ) : multiline ? (
          <p className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed">{value}</p>
        ) : (
          <p className="text-sm text-zinc-100 font-mono break-all">{value}</p>
        )}
      </div>
    </div>
  );
};

const ApplicationPacketModal = ({ grantId, isOpen, onClose }) => {
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("answers"); // answers | docs

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    axios.get(`${API}/applications/packet/${grantId}`)
      .then((r) => setPacket(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load packet"))
      .finally(() => setLoading(false));
  }, [isOpen, grantId]);

  const copyAll = async () => {
    if (!packet) return;
    const o = packet.org_basics;
    const s = packet.suggested_answers;
    const block = [
      `# ${packet.grant.title}`,
      ``,
      `## Organization`,
      `Name: ${o.org_name}`,
      `EIN: ${o.ein}`,
      `Address: ${o.full_address}`,
      `Phone: ${o.phone}`,
      `Email: ${o.email}`,
      `Website: ${o.website}`,
      `Founded: ${o.founding_year}`,
      `Annual budget: ${o.annual_budget}`,
      `Staff: ${o.staff_count}`,
      `Service area: ${s.service_area}`,
      `Focus areas: ${(o.focus_areas || []).join(", ")}`,
      ``,
      `## Mission`,
      s.mission_statement,
      ``,
      `## Project narrative`,
      s.project_narrative || "(No Flash Draft yet — generate one on the grant detail page)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(block);
      toast.success("Full packet copied — paste into a Google Doc to share with your team");
    } catch { toast.error("Couldn't copy"); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[92vh] overflow-y-auto w-[calc(100vw-1.5rem)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightning weight="fill" className="w-5 h-5 text-emerald-400" />
            One-Click Apply Prep
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-0.5">
            Every answer the funder will ask for, pre-filled. Click <strong className="text-zinc-300">Copy</strong> next to each field and paste into the portal.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-zinc-500">Loading your packet…</div>
        ) : !packet ? null : (
          <div className="space-y-4">
            {/* Grant strip */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3" data-testid="packet-grant-strip">
              <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-mono mb-1">Applying to</p>
              <p className="font-bold text-zinc-100">{packet.grant.title}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{packet.grant.funder || ""}</p>
              {packet.grant.application_url && (
                <a href={packet.grant.application_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 mt-2"
                   data-testid="open-portal-link">
                  Open funder's portal <ArrowSquareOut className="w-3.5 h-3.5" />
                </a>
              )}
            </div>

            {/* Profile completeness warning */}
            {packet.profile_completeness.percent < 100 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 flex items-start gap-2" data-testid="profile-warning">
                <Warning weight="fill" className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-amber-200 font-semibold">
                    Your profile is {packet.profile_completeness.percent}% complete
                  </p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    Fields marked "Not set" will be blank on every grant application. <Link to="/profile" className="text-emerald-400 underline">Complete your profile →</Link>
                  </p>
                </div>
              </div>
            )}

            {/* Tab switch */}
            <div className="inline-flex bg-zinc-900 border border-zinc-800 rounded-md p-1">
              <button onClick={() => setTab("answers")} className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition ${tab === "answers" ? "bg-emerald-500 text-black" : "text-zinc-400 hover:text-zinc-200"}`} data-testid="tab-answers">
                Pre-filled answers
              </button>
              <button onClick={() => setTab("docs")} className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition ${tab === "docs" ? "bg-emerald-500 text-black" : "text-zinc-400 hover:text-zinc-200"}`} data-testid="tab-docs">
                Documents <span className="opacity-60">({packet.documents.total_count})</span>
              </button>
            </div>

            {tab === "answers" ? (
              <div className="space-y-3" data-testid="answers-pane">
                <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Organization basics</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  <CopyField label="Legal name" value={packet.org_basics.org_name} testid="field-org-name" />
                  <CopyField label="EIN" value={packet.org_basics.ein} testid="field-ein" />
                  <CopyField label="Mailing address" value={packet.org_basics.full_address} testid="field-address" />
                  <CopyField label="Phone" value={packet.org_basics.phone} testid="field-phone" />
                  <CopyField label="Primary contact email" value={packet.org_basics.email} testid="field-email" />
                  <CopyField label="Website" value={packet.org_basics.website} testid="field-website" />
                  <CopyField label="Year founded" value={packet.org_basics.founding_year} testid="field-founded" />
                  <CopyField label="Annual operating budget" value={packet.org_basics.annual_budget} testid="field-annual-budget" />
                  <CopyField label="Staff count" value={packet.org_basics.staff_count} testid="field-staff" />
                  <CopyField label="Service area" value={packet.suggested_answers.service_area} testid="field-service-area" />
                </div>

                <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono pt-2">Narrative</p>
                <CopyField label="Project title" value={packet.suggested_answers.project_title} testid="field-project-title" />
                <CopyField label="Mission statement" value={packet.suggested_answers.mission_statement} multiline testid="field-mission" />
                <CopyField
                  label={`Project narrative (Flash Draft)${packet.has_draft ? "" : " — none yet"}`}
                  value={packet.suggested_answers.project_narrative}
                  multiline
                  testid="field-narrative"
                />
                <CopyField label="Primary contact name" value={packet.suggested_answers.primary_contact} testid="field-contact-name" />
                <CopyField label="Requested amount" value={packet.suggested_answers.requested_amount ? `$${packet.suggested_answers.requested_amount.toLocaleString?.() || packet.suggested_answers.requested_amount}` : ""} testid="field-amount" />

                <Button onClick={copyAll} variant="outline" className="w-full border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm mt-2" data-testid="copy-all-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy entire packet as text
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="docs-pane">
                <p className="text-xs text-zinc-500">Common documents most funders require. Click to download — opens in a new tab so you can drop straight into the portal upload field.</p>
                <div className="space-y-2">
                  {packet.documents.checklist.map((c) => {
                    const docs = packet.documents.categorized[c.key] || [];
                    return (
                      <div key={c.key} className="border border-zinc-800 rounded-sm p-3" data-testid={`doc-row-${c.key}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 flex-1">
                            {c.present ? (
                              <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                            ) : (
                              <Warning weight="fill" className="w-5 h-5 text-zinc-700 mt-0.5 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-zinc-100">{c.label}</p>
                              {c.present && docs.length > 0 ? (
                                <div className="mt-1 space-y-1">
                                  {docs.map((d) => (
                                    <a key={d.doc_id} href={`${API}${d.download_url}`} target="_blank" rel="noopener noreferrer"
                                       className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 truncate"
                                       data-testid={`download-${d.doc_id}`}>
                                      <Download className="w-3.5 h-3.5 flex-shrink-0" />
                                      <span className="truncate">{d.filename}</span>
                                    </a>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-zinc-500 mt-0.5">Not uploaded yet</p>
                              )}
                            </div>
                          </div>
                          {!c.present && (
                            <Link to="/documents">
                              <Button size="sm" variant="outline" className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm text-xs h-7">
                                Upload <ArrowUpRight className="w-3 h-3 ml-1" />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Link to="/documents" className="block">
                  <Button variant="outline" className="w-full border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm" data-testid="manage-docs-link">
                    <FileText className="w-4 h-4 mr-2" /> Manage all my documents
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationPacketModal;
